import {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "@/providers/auth-provider";
import { graphQLClient } from "@/lib/client-graphql";
import { apiGetReadMessages } from "@/lib/client-api";
import { compareName } from "@/lib/utils";
import { useSocket } from "@/providers/socket-provider";
import { signal } from "@preact/signals-react";
import { Button } from "@/components/ui/button";
import { setMainMenu, MAIN_MENUS, mainMenu } from "@/pages/layouts/main-header";
import { sendMessageToServiceWorker } from "@/features/service-worker-message";
import { cn } from "@/lib/utils";
import { getCachedChatsFromIDB, saveCachedChatsToIDB } from "@/lib/indexed-db";
import {
  getCachedMessagesFromIDB,
  saveCachedMessagesToIDB,
  deleteCachedMessagesFromIDB,
  MAX_MESSAGES_PER_CHAT,
} from "@/lib/idb-messages";

export const CHAT_EVENTS = [
  "CHAT_MESSAGE",
  "CHAT_READ",
  "CHAT_CREATE",
  "CHAT_DELETE",
  "CHAT_UPDATE",
  "CHAT_LEAVE",
  "FRIEND_DELETE",
] as const;

const ChatProviderContext = createContext<any>(undefined);

export const onNewMessage = signal<((message: any) => void) | null>(null);
export const hasNewMessage = signal<boolean>(false);

export function ChatProvider({ children, ...props }: { children: ReactNode }) {
  const { user } = useAuth();
  const {
    isConnected,
    sendMessage,
    registerMessageHandler,
    unregisterMessageHandler,
  } = useSocket();
  const [chats, setChats] = useState<any>(null);
  const [friends, setFriends] = useState<any>(null);
  const [currentChat, setCurrentChat] = useState<any>(null);
  const [popupMessage, setPopupMessage] = useState<any>(null);
  const [popupExiting, setPopupExiting] = useState(false);
  const [chatsHasNewMessage, setChatsHasNewMessage] = useState(false);
  const messagesMap = useRef(
    new Map<string, { messages: any[]; hasMore: boolean }>(),
  );
  const selectChatIdReserved = useRef<string | null>(null);
  const isLoadingChats = useRef<boolean>(false);

  let timerId: NodeJS.Timeout;
  function showPopupMessage(message: any, clearMS: number = 3000) {
    setPopupExiting(false);
    setPopupMessage(message);
    clearTimeout(timerId); // 이전 타이머 취소
    timerId = setTimeout(() => {
      setPopupExiting(true);
      // setPopupMessage(null);
    }, clearMS);
  }

  useEffect(() => {
    fetchChats(true);
  }, []);

  useEffect(() => {
    if (isConnected) {
      fetchChats();
    }
  }, [isConnected]);

  useEffect(() => {
    if (chats && user?._id) {
      saveCachedChatsToIDB(user._id, chats);
    }
  }, [chats, user?._id]);

  const getMessages = (
    chatId: string,
  ): { messages: any[]; hasMore: boolean } | null => {
    // 메모리 캐시 확인
    const memoryCache = messagesMap.current.get(chatId);
    if (memoryCache) {
      return memoryCache;
    }

    // 3. 캐시 없음
    return null;
  };

  const getMessagesFromIDB = async (
    chatId: string,
  ): Promise<{ messages: any[]; hasMore: boolean } | null> => {
    // IDB 캐시 확인
    if (user?._id) {
      const idbCache = await getCachedMessagesFromIDB(user._id, chatId);
      if (idbCache) {
        // 메모리에도 저장
        messagesMap.current.set(chatId, idbCache);
        return idbCache;
      }
    }

    // 캐시 없음
    return null;
  };

  const setMessages = (
    chatId: string,
    messages: { messages: any[]; hasMore: boolean },
  ) => {
    // 메모리 캐시
    messagesMap.current.set(chatId, messages);

    // IndexedDB 캐시
    if (user?._id) {
      // 500개 초과 시 자동 정리
      if (messages.messages.length > MAX_MESSAGES_PER_CHAT) {
        const trimmed = {
          ...messages,
          messages: messages.messages.slice(0, MAX_MESSAGES_PER_CHAT),
        };
        saveCachedMessagesToIDB(user._id, chatId, {
          ...trimmed,
          lastUpdated: Date.now(),
        });
      } else {
        // 백그라운드에서 저장 (await 불필요)
        saveCachedMessagesToIDB(user._id, chatId, {
          ...messages,
          lastUpdated: Date.now(),
        });
      }
    }
  };

  const fetchChats = useCallback(
    async (useCache = false) => {
      if (isLoadingChats.current) return;
      try {
        isLoadingChats.current = true;
        // 1. useCache가 true면 캐시 먼저 로드하여 UI에 표시
        if (useCache) {
          const cachedChats = await getCachedChatsFromIDB(user?._id);
          if (cachedChats) {
            setChats(cachedChats);
            console.log("[CHAT] Loaded chats from cache");
          }
        }

        // 2. 서버에서 최신 데이터 조회
        const { readMessages } = await apiGetReadMessages(user?.token);
        const data = await graphQLClient.fetchChats(true);
        const processedChats = data.map((chat: any) => ({
          ...chat,
          friend: !chat.isGroupChat
            ? chat.users.find((u: any) => u._id !== user?._id)
            : null,
          // 검색 최적화를 위한 Map 생성 (이 과정을 '인덱싱'이라고도 함)
          userMap: chat.users.reduce((acc: Record<string, any>, user: any) => {
            // acc(accumulator)는 누적된 결과물, user는 현재 순회 중인 개별 유저 객체
            acc[user._id] = {
              name: user.name,
              email: user.email,
              picture: user.picture,
              bgColor: user.data?.bgColor ?? null,
            };
            return acc;
          }, {}), // 초기값으로 빈 객체 {}를 설정
          hasNewMessage: hasChatNewMessage(chat, readMessages),
          readMessageId: readMessages.find((rm: any) => rm.chatId === chat._id)
            ?.messageId,
        }));
        console.log("[CHAT] fetchChats, ", processedChats);
        const sortedFriends = processedChats
          .filter((chat: any) => !chat.isGroupChat)
          .sort((a: any, b: any) => compareName(a.friend.name, b.friend.name));
        const sortedGroups = processedChats
          .filter((chat: any) => chat.isGroupChat)
          .sort((a: any, b: any) => compareName(a.chatName, b.chatName));
        const chats = {
          all: processedChats,
          // all: [...sortedFriends, ...sortedGroups],
          friends: sortedFriends,
          groups: sortedGroups,
        };

        // 3. UI 업데이트
        setChats(chats);

        // 선택된 대화가 있으면 업데이트.
        if (currentChat) {
          const updatedCurrentChat = chats.all.find(
            (chat: any) => chat._id === currentChat._id,
          );
          if (updatedCurrentChat) {
            setCurrentChat(updatedCurrentChat);
          } else {
            setCurrentChat(null);
          }
          console.log("[CHAT] currentChat is updated, ", updatedCurrentChat);
        }

        // 친구 목록도 업데이트
        await fetchFriends();
      } catch (error) {
        console.error("[CHAT] fetchChats failed: ", error);
      } finally {
        isLoadingChats.current = false;
      }
    },
    [user?._id, currentChat],
  );

  const fetchFriends = useCallback(async () => {
    try {
      const data = await graphQLClient.getFriends(true);
      console.log("[CHAT] fetchFriends : ", data);
      setFriends(data);
    } catch (error) {
      console.error("[CHAT] fetchChats failed: ", error);
    }
  }, []);

  const fetchMessages = useCallback(
    async ({
      chatId,
      loadFromStart = true,
    }: {
      chatId: string;
      loadFromStart?: boolean;
    }) => {
      try {
        const FETCH_COUNT = 50;
        const chatInfo =
          chats.all.find((chat: any) => chat._id === chatId) || null;

        // 1. 캐시 데이터 로드 (메모리 우선 후 IDB)
        const cachedChat =
          getMessages(chatId) ?? (await getMessagesFromIDB(chatId));
        const cachedMessages = cachedChat?.messages || [];

        // 2. 초기 로딩 시 최신 데이터 여부 확인 (최적화)
        if (loadFromStart && cachedMessages.length > 0) {
          const latestMessage = chatInfo?.latestMessage;
          // 내 로컬 최신 메시지와 서버의 최신 메시지 ID가 같으면 바로 리턴
          if (latestMessage._id === cachedMessages[0]?._id) {
            return cachedChat;
          }
        }

        // 3. 추가 로딩(무한스크롤) 시, '확실히' 더 가져올 데이터가 없을 때만 리턴
        // cachedChat이 없거나 hasMore가 undefined이면 서버 조회를 진행함
        if (!loadFromStart && cachedChat?.hasMore === false) {
          return cachedChat;
        }

        // 4. 요청 파라미터 구성
        const messageId = loadFromStart
          ? undefined
          : cachedMessages.at(-1)?._id; // .at(-1)은 마지막 요소, 없으면 undefined

        const endMessageId =
          loadFromStart && cachedMessages.length > 0
            ? cachedMessages[0]?._id
            : undefined;

        // 5. 서버 데이터 페칭
        const serverData = await graphQLClient.allMessages(
          {
            chatId,
            messageId,
            endMessageId,
            count: FETCH_COUNT,
          },
          true,
        );

        // 데이터 가공
        const processedMessages = serverData.map((msg: any) => ({
          ...msg,
          isMe: msg.sender._id === user?._id,
        }));

        let finalMessages;
        let finalHasMore;

        if (loadFromStart) {
          const includesEndMessage = serverData.some(
            (m: any) => m._id === endMessageId,
          );
          const isFullCount = serverData.length === FETCH_COUNT;

          if (isFullCount && !includesEndMessage) {
            // 갭이 너무 커서 이어붙일 수 없는 경우
            finalMessages = processedMessages;
            finalHasMore = true;
          } else {
            // 갭이 없거나 메워진 경우: 서버 데이터에서 중복되는 마지막 하나를 제외하고 병합
            // serverData의 마지막 요소가 cachedMessages[0]와 같으므로 제외
            const newUniqueMessages = includesEndMessage
              ? processedMessages.filter((m: any) => m._id !== endMessageId)
              : processedMessages;

            finalMessages = [...newUniqueMessages, ...cachedMessages];
            finalHasMore = cachedChat
              ? cachedChat.hasMore
              : serverData.length === FETCH_COUNT;
          }
        } else {
          // [시나리오: 무한 스크롤 과거 데이터 추가]
          finalMessages = [...cachedMessages, ...processedMessages];
          finalHasMore = serverData.length === FETCH_COUNT;
        }

        const newData = {
          messages: finalMessages,
          hasMore: finalHasMore,
        };

        // 6. 결과 저장 (메모리 + IndexedDB)
        setMessages(chatId, newData);
        return newData;
      } catch (error) {
        console.error("[CHAT] fetchMessages error:", error);
        const fallback = await getMessagesFromIDB(chatId);
        if (fallback) setMessages(chatId, fallback);
        return fallback;
      }
    },
    [user?._id, chats, getMessages, setMessages],
  );

  const handleChatMessage = useCallback(
    async (data: any) => {
      // 채팅 메시지 처리
      console.log("[CHAT] CHAT_MESSAGE:", data);
      // messages 업데이트 등의 처리
      const message = data.message;
      if (!message) {
        console.error("[CHAT] received message is null");
        return;
      }
      const processedMessage = {
        ...message,
        isMe: message.sender._id === user._id,
        tempId: data.tempId,
      };
      const chatId = processedMessage.chat._id;
      const isCurrentChat = currentChat?._id === chatId;
      const isInChatTab = mainMenu.value === MAIN_MENUS.CHATS;

      // 채팅 리스트 업데이트.
      setChats((prevChats: any) => ({
        ...prevChats,
        all: prevChats.all.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                latestMessage: processedMessage,
                hasNewMessage: !isCurrentChat,
              }
            : chat,
        ),
        friends: prevChats.friends.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                latestMessage: processedMessage,
                hasNewMessage: !isCurrentChat,
              }
            : chat,
        ),
        groups: prevChats.groups.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                latestMessage: processedMessage,
                hasNewMessage: !isCurrentChat,
              }
            : chat,
        ),
      }));
      // 캐싱된 메시지 업데이트.
      const cachedChat = getMessages(chatId);
      if (cachedChat) {
        setMessages(chatId, {
          messages: [processedMessage, ...cachedChat.messages],
          hasMore: cachedChat.hasMore,
        });
      }
      // 채팅방에 입장한 상태라면,
      if (isCurrentChat) {
        // 메시지 전송.
        onNewMessage.value?.(processedMessage);
        // 메시지 읽음 처리.
        const messageId = processedMessage._id;
        sendChatRead(chatId, messageId, false);

        if (!isInChatTab) {
          // 다른 탭에서 메시지를 받았을 경우, 팝업으로 표시한다.
          showPopupMessage(processedMessage);
        }
      } else {
        // 다른 채팅방의 메시지는 팝업으로 표시한다.
        showPopupMessage(processedMessage);
      }
    },
    [user?._id, currentChat],
  );

  const handleChatRead = useCallback(
    async (data: any) => {
      // 채팅 읽음 처리
      console.log("[CHAT] CHAT_READ:", data);

      // 읽은 메시지 정보
      const chatId = data.chatId;
      const messageId = data.messageId;
      // 채팅 리스트 업데이트.
      setChats((prevChats: any) => ({
        ...prevChats,
        all: prevChats.all.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                hasNewMessage: chat.latestMessage?._id !== messageId,
              }
            : chat,
        ),
        friends: prevChats.friends.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                hasNewMessage: chat.latestMessage?._id !== messageId,
              }
            : chat,
        ),
        groups: prevChats.groups.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                hasNewMessage: chat.latestMessage?._id !== messageId,
              }
            : chat,
        ),
      }));
    },
    [chats],
  );

  const updateChatRead = useCallback(
    (chatId: string) => {
      if (!chatId) return;
      // 채팅 리스트 업데이트.
      setChats((prevChats: any) => ({
        ...prevChats,
        all: prevChats.all.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                readMessageId: chat.latestMessage?._id,
              }
            : chat,
        ),
        friends: prevChats.friends.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                readMessageId: chat.latestMessage?._id,
              }
            : chat,
        ),
        groups: prevChats.groups.map((chat: any) =>
          chat._id === chatId
            ? {
                ...chat,
                readMessageId: chat.latestMessage?._id,
              }
            : chat,
        ),
      }));
    },
    [chats],
  );

  const handleChatUpdate = useCallback(
    async (data: any) => {
      const type = data.type;
      console.log(`[CHAT] ${type}: `, data);
      if (type === "CHAT_DELETE" || type === "FRIEND_DELETE") {
        // 삭제된 대화방에 참여중이라면 나간다.
        leaveChat(data.chatId);
        // 캐시를 삭제한다.
        await deleteCachedMessagesFromIDB(user?._id, data.chatId);
      }
      await fetchChats();
    },
    [fetchChats],
  );

  useEffect(() => {
    // CHAT_MESSAGE 핸들러 등록
    CHAT_EVENTS.forEach((event) => {
      if (event === "CHAT_MESSAGE") {
        registerMessageHandler(event, handleChatMessage);
      } else if (event === "CHAT_READ") {
        registerMessageHandler(event, handleChatRead);
      } else {
        registerMessageHandler(event, handleChatUpdate);
      }
    });
    // Cleanup function
    return () => {
      // 컴포넌트 언마운트시 핸들러 제거
      CHAT_EVENTS.forEach((event) => {
        unregisterMessageHandler(event);
      });
    };
  }, [handleChatMessage, handleChatRead, handleChatUpdate]);

  const hasChatNewMessage = (chat: any, readMessages: any[]) => {
    const readMessage = readMessages.find((rm: any) => rm.chatId === chat._id);
    if (!readMessage) {
      console.warn("[CHAT] hasChatNewMessage, chat not found");
      return false;
    }
    return readMessage.messageId !== chat.latestMessage?._id;
  };

  const createChat = async (chatName: string, users: any) => {
    const userIds = users.map((user: any) => user._id as string);
    const result = await graphQLClient.createGroupChat({
      chatName,
      users: userIds,
    });
    console.log("[CHAT] created chat: ", result);
    return result;
  };

  const updateChat = async (
    chatId: string,
    chatName: string,
    admin: string,
    users: any,
  ) => {
    const userIds = users.map((user: any) => user._id as string);
    const result = await graphQLClient.updateGroupChat({
      chatId,
      chatName,
      admin,
      users: userIds,
    });
    console.log("[CHAT] updated chat: ", result);
    return result;
  };

  const deleteChat = async (chatId: string) => {
    const result = await graphQLClient.deleteGroupChat({
      chatId,
    });
    selectChat(null);
    console.log("[CHAT] deleted chat: ", result);
    return result;
  };

  const removeUser = async (chatId: string, userId: string) => {
    const result = await graphQLClient.removeUserFromGroupChat({
      chatId,
      userId,
    });
    console.log("[CHAT] removed user: ", result);
    return result;
  };

  const selectChat = (selectedChat: any) => {
    if (currentChat?._id === selectedChat?._id) return;
    setCurrentChat(selectedChat);
    // 새로운 메시지가 있을 경우, 읽음 처리.
    if (selectedChat?._id && selectedChat.hasNewMessage) {
      sendChatRead(selectedChat?._id, selectedChat.latestMessage?._id);
    }
    // 채팅방에서 나갈 경우, 여기까지 읽음 업데이트.
    if (currentChat?._id !== selectedChat?._id) {
      updateChatRead(currentChat?._id);
    }

    // 서비스워커에 채팅방 참여여부를 전달한다.
    sendMessageToServiceWorker({
      type: "CLIENT_SELECT_CHAT",
      chat: selectedChat,
    });
  };

  const selectChatId = (chatId: any) => {
    if (!chats) {
      // chats가 아직 로드되지 않은 경우, 예약만 걸어둠
      selectChatIdReserved.current = chatId;
      return;
    }
    const findChat = chats.all.find((chat: any) => chat._id === chatId);
    if (findChat) {
      selectChat(findChat);
    } else {
      console.warn("[CHAT] selectChatId, chat not found");
    }
  };

  useEffect(() => {
    // 채팅방 중에 새로운 메시지를 받은 경우,
    if (chats) {
      const anyChatHasNewMessage = chats.all.some(
        (chat: any) => chat.hasNewMessage,
      );
      setChatsHasNewMessage(anyChatHasNewMessage);
    }

    // 공유 기능으로 채팅방을 선택할 때, 채팅방 정보를 저장해놓았다가 채팅방 정보가 준비되면 입장한다.
    if (chats && selectChatIdReserved.current) {
      selectChatId(selectChatIdReserved.current);
      selectChatIdReserved.current = null;
    }
  }, [chats]);

  const enterChat = (chatId: any) => {
    setMainMenu(MAIN_MENUS.CHATS);
    selectChatId(chatId);
  };

  const leaveChat = (chatId: any = null) => {
    // chatId가 주어지면 해당 채팅방에 입장중일때만 나간다.
    if (chatId && chatId !== currentChat?._id) return;
    // chatId가 주어지지 않으면 현재 채팅방에서 나간다.
    selectChat(null);
  };

  const sendChatMessage = (message: string, tempId: string) => {
    if (!currentChat) {
      console.warn("[CHAT] 메시지를 보내려면 채팅을 선택해야 합니다.");
      return;
    }
    const payload = {
      type: "CHAT_MESSAGE",
      message: message,
      chatId: currentChat._id,
      tempId: tempId,
    };
    sendMessage(payload);
    console.log("[CHAT] Send chat message:", payload);
  };

  const sendChatRead = (
    chatId: string,
    messageId: string,
    sendToMe: boolean = true,
  ) => {
    if (isConnected) {
      const payload = {
        type: "CHAT_READ",
        chatId: chatId,
        messageId: messageId,
        sendToMe: sendToMe,
      };
      sendMessage(payload);
      console.log("[CHAT] Send chat read:", payload);
    } else {
      console.warn("[CHAT] 서버에 연결되지 않았습니다.");
    }
  };

  const value = {
    chats,
    friends,
    fetchChats,
    fetchFriends,
    fetchMessages,
    currentChat,
    createChat,
    updateChat,
    deleteChat,
    removeUser,
    selectChat,
    selectChatId,
    enterChat,
    leaveChat,
    sendChatMessage,
    chatsHasNewMessage,
  };

  return (
    <ChatProviderContext.Provider {...props} value={value}>
      {children}
      {popupMessage && (
        <div
          className={cn(
            "fixed top-2 right-2 z-50",
            popupExiting ? "animate-slide-up-exit" : "animate-slide-down",
          )}
          // 확실히 퇴장 애니메이션 후 상태를 업데이트
          onAnimationEnd={() => {
            if (popupExiting) setPopupMessage(null);
          }}
        >
          <Button onClick={() => enterChat(popupMessage.chat._id)}>
            {popupMessage.sender.name}: {popupMessage.content}
          </Button>
        </div>
      )}
    </ChatProviderContext.Provider>
  );
}

export const useChats = () => {
  const context = useContext(ChatProviderContext);
  if (!context) {
    throw new Error("[CHAT] useChat must be used within a ChatProvider");
  }
  return context;
};
